import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiService from '../services/api';

export interface SystemStatus {
  uptime: string;
  status: string;
  version?: string;
  environment?: string;
}

export interface QueueStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface CostData {
  daily: {
    spent: number;
    budget: number;
    percentage: number;
    remaining: number;
  };
  monthly: {
    spent: number;
    budget: number;
    percentage: number;
    remaining: number;
  };
  recentCosts?: Array<{
    date: string;
    cost: number;
    description?: string;
    operation?: string;
    model?: string;
    totalTokens?: number;
  }>;
  operationBreakdown?: Record<string, number>;
  operationCounts?: Record<string, number>;
}

export interface SystemContextValue {
  // State
  systemStatus: SystemStatus | null;
  queueStats: QueueStats | null;
  costData: CostData | null;
  loading: boolean;
  error: string | null;
  isProcessing: boolean;
  lastRefreshed: Date | null;
  pollingMs: number;
  
  // Actions
  fetchSystemData: () => void;
  fetchCostData: () => void;
  triggerNewsScraping: (sourceId?: number) => Promise<void>;
  triggerRiskCalculation: () => Promise<void>;
  setIsProcessing: (processing: boolean) => void;
  
  // Real-time updates
  startPolling: () => void;
  stopPolling: () => void;
  startCostPolling: () => void;
  stopCostPolling: () => void;
}

const SystemContext = createContext<SystemContextValue | undefined>(undefined);

export function useSystem(): SystemContextValue {
  const context = useContext(SystemContext);
  if (!context) {
    throw new Error('useSystem must be used within a SystemProvider');
  }
  return context;
}

interface SystemProviderProps {
  children: React.ReactNode;
}

export const POLLING_INTERVAL = 30000; // 30 seconds

export function SystemProvider({ children }: SystemProviderProps) {
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [costData, setCostData] = useState<CostData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [costPollingInterval, setCostPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const inFlightRef = React.useRef(false);
  const inFlightCostRef = React.useRef(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchSystemData = useCallback(async () => {
    if (inFlightRef.current) return; // de-dup concurrent fetches
    try {
      setLoading(true);
      setError(null);
      inFlightRef.current = true;
      
      const [status, queue] = await Promise.all([
        apiService.getSystemStatus(),
        apiService.getQueueStats(),
      ]);
      
      setSystemStatus(status);
      setQueueStats(queue);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Failed to fetch system data:', err);
      setError('Failed to load system data');
      // Stop polling on authentication errors to prevent server log spam
      const status = (err as any)?.response?.status;
      if (status === 401 && pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  const fetchCostData = useCallback(async () => {
    if (inFlightCostRef.current) return;
    try {
      inFlightCostRef.current = true;
      const costs = await apiService.getCostData();
      setCostData(costs);
    } catch (err) {
      console.error('Failed to fetch cost data:', err);
    } finally {
      inFlightCostRef.current = false;
    }
  }, []);

  const triggerNewsScraping = useCallback(async (sourceId?: number) => {
    try {
      setIsProcessing(true);
      await apiService.triggerNewsScraping(sourceId);
      // Refresh data to show updated queue stats
      fetchSystemData();
    } catch (err) {
      console.error('Failed to trigger news scraping:', err);
      throw new Error('Failed to trigger news scraping');
    } finally {
      setIsProcessing(false);
    }
  }, [fetchSystemData]);

  const triggerRiskCalculation = useCallback(async () => {
    try {
      setIsProcessing(true);
      await apiService.triggerRiskCalculation();
      // Refresh data to show updated queue stats
      fetchSystemData();
    } catch (err) {
      console.error('Failed to trigger risk calculation:', err);
      throw new Error('Failed to trigger risk calculation');
    } finally {
      setIsProcessing(false);
    }
  }, [fetchSystemData]);

  const startPolling = useCallback(() => {
    if (pollingInterval) return; // Already polling
    
    const interval = setInterval(() => {
      // Avoid stacking intervals or overlapping requests
      // Skip background polling when tab is hidden
      if (document.hidden) return;
      if (!inFlightRef.current) fetchSystemData();
    }, POLLING_INTERVAL);
    
    setPollingInterval(interval);
  }, [fetchSystemData, pollingInterval]);

  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingInterval]);

  const startCostPolling = useCallback(() => {
    if (costPollingInterval) return;
    const interval = setInterval(() => {
      if (!inFlightCostRef.current) fetchCostData();
    }, POLLING_INTERVAL);
    setCostPollingInterval(interval);
  }, [costPollingInterval, fetchCostData]);

  const stopCostPolling = useCallback(() => {
    if (costPollingInterval) {
      clearInterval(costPollingInterval);
      setCostPollingInterval(null);
    }
  }, [costPollingInterval]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const contextValue: SystemContextValue = {
    // State
    systemStatus,
    queueStats,
    costData,
    loading,
    error,
    isProcessing,
    lastRefreshed,
    pollingMs: POLLING_INTERVAL,
    
    // Actions
    fetchSystemData,
    fetchCostData,
    triggerNewsScraping,
    triggerRiskCalculation,
    setIsProcessing,
    
    // Real-time updates
    startPolling,
    stopPolling,
    startCostPolling,
    stopCostPolling
  };

  return (
    <SystemContext.Provider value={contextValue}>
      {children}
    </SystemContext.Provider>
  );
}
